/**
 * Username Component
 * Notes+ - Bosph Technologies
 * 
 * Global reusable component for displaying usernames with Plus member visual identity.
 * Automatically applies Plus gold color (#FFD700) for Plus subscribers.
 * 
 * Supports future expansion for Premium/Admin roles.
 */

import React from 'react';
import { isPlusMember, isAdminMember } from '../../utils/userDisplay';
import i18n from '../../i18n';

/**
 * Get display name from user object (nickname > name > email prefix)
 * @param {Object} user - User object
 * @returns {string} - Display name
 */
const getDisplayName = (user) => {
  if (!user) return i18n.t('common:unknownUser');

  // Priority 1: Nickname (user-defined display name)
  if (user?.nickname && user.nickname.trim() !== '') {
    return user.nickname;
  }

  // Priority 2: Name
  if (user?.name && user.name.trim() !== '') {
    return user.name;
  }

  // Priority 3: Email prefix with first letter capitalized
  if (user?.email) {
    const emailPrefix = user.email.split('@')[0];
    return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
  }

  return i18n.t('common:unknownUser');
};

/**
 * Username Component
 * @param {Object} props
 * @param {Object} props.user - User object (required)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.variant - Display variant: 'default' | 'bold' | 'subtle'
 * @param {boolean} props.showPlusBadge - Show Plus badge icon (default: false)
 * @param {React.ReactNode} props.children - Optional children to render instead of display name
 */
const Username = ({
  user,
  className = '',
  variant = 'default',
  showPlusBadge = false,
  plain = false,
  children
}) => {
  if (!user) {
    return <span className={className}>{i18n.t('common:unknownUser')}</span>;
  }

  const displayName = children || getDisplayName(user);

  let baseClasses = 'text-white';
  switch (variant) {
    case 'bold':
      baseClasses = 'text-white font-bold';
      break;
    case 'subtle':
      baseClasses = 'text-white/80 font-medium';
      break;
    default:
      baseClasses = 'text-white';
      break;
  }

  const combinedClasses = `${baseClasses} ${className}`.trim();

  // plain=true: grup sohbeti gibi bağlamlarda global rol rengi istenmiyor
  if (plain) {
    return <span className={combinedClasses}>{displayName}</span>;
  }

  const isAdmin = isAdminMember(user);
  const isPlus = isPlusMember(user);

  if (isAdmin) {
    return (
      <span
        className={`username-admin-member ${combinedClasses}`.trim()}
        data-admin-member="true"
        data-plus-member="true"
      >
        {displayName}
        {showPlusBadge && (
          <span className="ml-1.5 text-xs admin-badge-prestige px-1.5 py-0.5 rounded" title={i18n.t('common:founderAdmin')}>
            👑
          </span>
        )}
      </span>
    );
  }

  if (isPlus) {
    return (
      <span
        className={`username-plus-member ${combinedClasses}`.trim()}
        data-plus-member="true"
      >
        {displayName}
        {showPlusBadge && (
          <span className="ml-1.5 text-xs plus-badge-prestige px-1.5 py-0.5 rounded" title={i18n.t('common:plusMember')}>
            ⭐
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={combinedClasses}>
      {displayName}
    </span>
  );
};

export default Username;
export { getDisplayName };
